import { RuleContext, RuleNode, ValueNode } from '@navstation-portal/reporting/data-model/ui';
import { evaluate, readCarryOverField, readReportField, readVesselField, readVisibilityRule } from './evaluate';
import { resolveValue } from './resolve-value';

/** The symbol each order shows in the tooltip. */
const ORDER_SYMBOL = { lt: '<', lte: '<=', gt: '>', gte: '>=' } as const;

/**
 * Describes one side of a comparison for the tooltip.
 *
 * A field names its path and the value it holds, in the shape the rule lines
 * use, because a reader wants to know which field was empty. A fixed value and
 * a branch node each name the value they gave: a branch that shows the tree
 * which chose it would turn one line into several, and the value is what the
 * comparison used.
 */
function describeValue(node: ValueNode, context: RuleContext): string {
  const value = formatValue(resolveValue(node, context));
  return node.type === 'reportField' ? `${node.path} = ${value}` : value;
}

/**
 * A line in a rule explanation. It names one node and the value the node
 * returned for the given context. The debug UI reads these lines.
 */
export interface RuleExplanationLine {
  /** The node type, for example 'const' or 'reportTypeIn'. */
  type: string;
  /** The node's inputs, and the value it read where it reads one. */
  detail: string;
  /** The boolean the node returned for the context. */
  result: boolean;
  /**
   * How deep the node sits in the tree. A composite node, such as `and` or
   * `not`, gives its children the next depth. The debug UI indents by this, so
   * a reader can see which child belongs to which parent.
   */
  depth: number;
}

/**
 * Formats a value a node read, for the detail of a line. A string keeps its
 * quotes, so an empty string and a missing value do not look the same.
 *
 * `explainValue` formats the same way, so one reader reads a rule line and a
 * value line without learning two shapes.
 */
export function formatValue(value: unknown): string {
  if (value === undefined) return '(unset)';
  if (value === null) return '(null)';
  if (typeof value === 'string') return `"${value}"`;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return '(object)';
  return String(value);
}

/**
 * DEV-ONLY. The item a rule belongs to, for the form-rule debug UI. It names
 * the item and reports which label the form shows.
 *
 * `explainRule` never sets this. It is a pure function: it knows a node and a
 * context, and it knows no tab id, no section id, and no label. The report
 * store adds this block, because the store knows all of them.
 */
export interface RuleExplanationSubject {
  /** The dotted path, for example 'operational.vesselDetails.reportNumber'. */
  path: string;
  /** The label the form shows. Null when the caller gave no label. */
  label: string | null;
  /**
   * Where the label came from. 'db' is a plain template label, 'db-rule' is a
   * template label whose text a case list chose, and 'fallback' is the text in
   * the markup.
   */
  labelSource: 'db' | 'db-rule' | 'fallback';
  /**
   * How a label tree chose its text, from `explainLabel`. It is empty for a
   * plain label and for a label the markup holds, because neither has anything
   * to explain.
   */
  labelLines: RuleExplanationLine[];
  /**
   * The item's other rules, each with the name the tooltip shows for it. An
   * item can hold more than one rule, and the tooltip reports the one it was
   * asked for first, then these. It is optional: a caller that asks about one
   * rule alone leaves it out.
   */
  otherRules?: NamedRuleExplanation[];
  /**
   * The item's value nodes, each with the name the tooltip shows for it. A
   * value node answers with a number or a date, not with a boolean, so it
   * carries the value it reached in place of a true or a false. It is optional:
   * a caller that asks about a rule alone leaves it out.
   */
  valueRules?: NamedValueExplanation[];
  /**
   * The item's carry-over, from the template. It is optional: a caller that
   * asks about a rule alone leaves it out.
   *
   * Unlike the bounds, it shows on every field, because carry-over applies to
   * every field where a bound applies to a number field and a date field
   * alone. See CarryOverExplanation.
   */
  carryOver?: CarryOverExplanation;
}

/**
 * What the carry-over of one field says, for the debug tooltip.
 *
 * Carry-over is the one rule that writes, so the block reports what the button
 * would put in before anyone presses it, and why it would put nothing in.
 */
export interface CarryOverExplanation {
  /**
   * The modes the entry sets, for the block header. It reads '(none)' where
   * the template holds no entry, and 'never' where it holds one with both
   * flags false. The two are different facts: one is a template that says
   * nothing, the other is a template that says never.
   */
  modes: string;
  /**
   * What the block says below the header. It is the value the carry-over would
   * write, '(none)' where the value node gives none, or the reason a lock
   * stops the button.
   *
   * It is null where the template holds no entry, so such a field shows a
   * one-line block.
   */
  detail: string | null;
  /**
   * How the value tree reached its answer, from `explainValue`. It is empty
   * where the template holds no entry.
   */
  lines: RuleExplanationLine[];
}

/** One rule of an item, with the name the tooltip shows for it. */
export interface NamedRuleExplanation {
  /** The rule name, for example 'isFieldRequired'. */
  name: string;
  explanation: RuleExplanation;
}

/** One value node of an item, with the name the tooltip shows for it. */
export interface NamedValueExplanation {
  /** The value name, for example 'min'. */
  name: string;
  /**
   * The value the node reached, formatted for a reader. It is null when the
   * node gives no value, so the form falls back to the bound in its markup.
   */
  value: string | null;
  /**
   * The bound the markup holds, formatted for a reader. It is the bound the
   * form applies whenever `value` is null, and it is null when the markup
   * holds none.
   *
   * It stays a separate property from `value`, so a reader tells a bound the
   * template set from one the markup holds. The two never both apply.
   */
  fallback: string | null;
  /**
   * True when the caller stated what its markup holds. A null `fallback` then
   * means the field has no bound at all.
   *
   * It is false when the caller stated nothing, which is what a field that has
   * not moved its bounds to the template passes. Such a field usually holds a
   * bound in its markup, so the tooltip must not report it as unbounded.
   */
  fallbackStated: boolean;
  /** How the value tree reached it, from `explainValue`. */
  lines: RuleExplanationLine[];
}

/**
 * The full explanation of one rule. `result` is the rule's boolean. `lines`
 * breaks down each node. `missing` is true when no rule exists, so the default
 * applies.
 *
 * `subject` is optional and dev-only. `explainRule` leaves it out. The report
 * store adds it for the debug UI.
 */
export interface RuleExplanation {
  result: boolean;
  missing: boolean;
  lines: RuleExplanationLine[];
  subject?: RuleExplanationSubject;
}

/**
 * What a rule target does when the template holds no rule for it. The visible
 * rules fail open and the required rule fails closed, so the two show a
 * different default and a different line.
 */
export interface MissingRuleDefault {
  /** What the target returns with no rule. */
  result: boolean;
  /** The line the explanation shows in place of a tree. */
  detail: string;
}

/** The default for a visibility rule: no rule shows the item. */
export const MISSING_VISIBLE: MissingRuleDefault = { result: true, detail: 'no rule, so the item shows' };

/** The default for a required rule: no rule leaves the field optional. */
export const MISSING_REQUIRED: MissingRuleDefault = { result: false, detail: 'no rule, so the field is optional' };

/** The default for a read-only rule: no rule leaves the field open to change. */
export const MISSING_READ_ONLY: MissingRuleDefault = { result: false, detail: 'no rule, so the user can change it' };

/** The default for a disabled rule: no rule leaves the field enabled. */
export const MISSING_DISABLED: MissingRuleDefault = {
  result: false,
  detail: 'no rule, so the field is not disabled',
};

/** The default for an expected rule: no rule expects nothing, so no warning shows. */
export const MISSING_EXPECTED: MissingRuleDefault = {
  result: false,
  detail: 'no rule, so the field expects nothing',
};

/**
 * Explains how a rule evaluates for a context. It returns the rule's boolean
 * and a per-node breakdown. This drives the form-rule debug UI. It does not
 * change how a rule evaluates; it reuses `evaluate` for the result.
 *
 * A null node means no rule exists, and `missing` reports the default the
 * target takes then. It is visible unless the caller names another.
 */
export function explainRule(
  node: RuleNode | null | undefined,
  context: RuleContext,
  missing: MissingRuleDefault = MISSING_VISIBLE
): RuleExplanation {
  if (!node) {
    return {
      result: missing.result,
      missing: true,
      lines: [{ type: 'default', detail: missing.detail, result: missing.result, depth: 0 }],
    };
  }

  const result = evaluate(node, context);
  return { result, missing: false, lines: explainNode(node, context, 0) };
}

/**
 * Explains one node to one or more lines. Most nodes give one line, and name
 * the value they read so a reader sees the input as well as the answer. A
 * composite node gives a parent line and its children at the next depth.
 */
export function explainNode(node: RuleNode, context: RuleContext, depth: number): RuleExplanationLine[] {
  switch (node.type) {
    case 'const':
      return [{ type: node.type, detail: String(node.value), result: node.value, depth }];
    case 'reportTypeIn': {
      const reportType = context.reportType;
      const list = node.reportTypes.join(', ');
      return [
        {
          type: node.type,
          detail: `reportType = ${formatValue(reportType)} in [${list}]`,
          result: evaluate(node, context),
          depth,
        },
      ];
    }
    case 'previousReportTypeIn': {
      const list = node.reportTypes.join(', ');
      return [
        {
          type: node.type,
          detail: `previousReportType = ${formatValue(context.previousReportType)} in [${list}]`,
          result: evaluate(node, context),
          depth,
        },
      ];
    }
    case 'isInternalAdmin':
      return [
        {
          type: node.type,
          detail: `isInternalAdmin = ${context.isInternalAdmin}`,
          result: evaluate(node, context),
          depth,
        },
      ];
    case 'isAdmin':
      return [
        {
          type: node.type,
          detail: `isAdmin = ${context.isAdmin}`,
          result: evaluate(node, context),
          depth,
        },
      ];
    case 'vesselFieldTruthy':
      return [
        {
          type: node.type,
          detail: `${node.path} = ${formatValue(readVesselField(context, node.path))}`,
          result: evaluate(node, context),
          depth,
        },
      ];
    case 'vesselFieldIn':
      return [
        {
          type: node.type,
          detail: `${node.path} = ${formatValue(readVesselField(context, node.path))} in [${node.values.join(', ')}]`,
          result: evaluate(node, context),
          depth,
        },
      ];
    case 'tabVisible':
    case 'sectionVisible': {
      // The line gives the answer of the rule, not its tree. The tooltip of
      // that tab or section explains the tree.
      const subject = node.type === 'tabVisible' ? `tab ${node.tab}` : `section ${node.path}`;
      const found = readVisibilityRule(node, context) !== undefined;
      return [
        {
          type: node.type,
          detail: found ? subject : `${subject} (no rule)`,
          result: evaluate(node, context),
          depth,
        },
      ];
    }
    case 'reportFieldTruthy':
      return [
        {
          type: node.type,
          detail: `${node.path} = ${formatValue(readReportField(context, node.path))}`,
          result: evaluate(node, context),
          depth,
        },
      ];
    case 'carryOverFieldTruthy':
      // The word 'previous' in front of the path, as the carryOverField value
      // line has, because the path looks the same as a reportFieldTruthy path.
      return [
        {
          type: node.type,
          detail: `previous ${node.path} = ${formatValue(readCarryOverField(context, node.path))}`,
          result: evaluate(node, context),
          depth,
        },
      ];
    case 'reportFieldEquals': {
      // The line names the property the node read, so a reader can see which
      // part of a dropdown value the rule asks about.
      const field = node.valueKey === undefined ? node.path : `${node.path}.${node.valueKey}`;
      return [
        {
          type: node.type,
          detail: `${field} = ${formatValue(readReportField(context, node.path, node.valueKey))}, equals ${formatValue(
            node.equals
          )}`,
          result: evaluate(node, context),
          depth,
        },
      ];
    }
    case 'compare':
      // One line, because a comparison is one question. It names each side with
      // the value it read, so a reader tells a rule that is false from a field
      // the user has not filled. The two read alike in the answer alone.
      return [
        {
          type: node.type,
          detail: `${describeValue(node.left, context)} ${ORDER_SYMBOL[node.op]} ${describeValue(node.right, context)}`,
          result: evaluate(node, context),
          depth,
        },
      ];
    case 'not': {
      const parent: RuleExplanationLine = {
        type: node.type,
        // It names what the node does, as 'and' and 'or' do. The mark already
        // says what this node returned, and the child line says what the child
        // returned, so a detail that restates either one reads as a third,
        // disagreeing answer.
        detail: 'inverts the rule below',
        result: evaluate(node, context),
        depth,
      };
      return [parent, ...explainNode(node.node, context, depth + 1)];
    }
    case 'and': {
      const parent: RuleExplanationLine = {
        type: node.type,
        detail: `all of ${node.nodes.length}`,
        result: evaluate(node, context),
        depth,
      };
      const children = node.nodes.flatMap((child) => explainNode(child, context, depth + 1));
      return [parent, ...children];
    }
    case 'or': {
      const parent: RuleExplanationLine = {
        type: node.type,
        detail: `any of ${node.nodes.length}`,
        result: evaluate(node, context),
        depth,
      };
      const children = node.nodes.flatMap((child) => explainNode(child, context, depth + 1));
      return [parent, ...children];
    }
    default:
      return assertNever(node);
  }
}

function assertNever(node: never): never {
  throw new Error(`Unknown rule node type: ${String((node as { type: string }).type)}`);
}
